import { useCallback, useEffect, useMemo, useState } from "react";

import { Button, Col, Row, Segmented } from "antd";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import Title from "antd/es/typography/Title";
import { toast } from "utils/toast";

import usePageLoading from "hooks/usePageLoading";

import LandlordsTable from "js/components/Landlords/LandlordTable/LandlordsTable";
import { ModernLandlordModal } from "js/components/Landlords/ModernLandlordModal";
import {
  PageLoadingContainer,
  SkeletonBlock,
  SkeletonHeading,
  SkeletonInput,
  SlowStateBanner,
  TableSkeleton,
} from "js/components/Loading";
import TableSearch from "js/components/TableSearch";
import { Landlord, PaginationTable } from "js/components/types";

import { LegalType } from "../../../hooks/useLandlordSelect";
import {
  useCreateLandlord,
  useLandlords,
  useUpdateLandlord,
} from "../../../hooks/useLandlords";

const LandlordsDashboard = () => {
  const screen = useBreakpoint();
  const [searchValue, setSearchValue] = useState("");
  const [legalType, setLegalType] = useState<LegalType>("pf");
  const [isLandlordModalOpen, setIsLandlordModalOpen] = useState(false);
  const [selectedLandlord, setSelectedLandlord] = useState<Landlord | {}>({});
  const [paginationPF, setPaginationPF] = useState<PaginationTable>({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [paginationPM, setPaginationPM] = useState<PaginationTable>({
    current: 1,
    pageSize: 15,
    total: 0,
  });

  const pagination = legalType === "pf" ? paginationPF : paginationPM;
  const setPagination = legalType === "pf" ? setPaginationPF : setPaginationPM;

  const {
    data: landlordsData,
    isLoading,
    refetch: refetchLandlords,
  } = useLandlords({
    current: pagination.current,
    pageSize: pagination.pageSize,
  });
  const createMutation = useCreateLandlord();
  const updateMutation = useUpdateLandlord();

  const loading =
    isLoading || createMutation.isPending || updateMutation.isPending;

  const { showSkeleton, showSlowState, dismissSlowState } = usePageLoading({
    isLoading,
  });

  const handleRetryFetch = () => {
    dismissSlowState();
    void refetchLandlords();
  };

  const slowStateBanner = showSlowState ? (
    <SlowStateBanner
      actions={
        <Button
          size="small"
          style={{
            borderColor: "#fcd34d",
            color: "#92400e",
            backgroundColor: "#fff7ed",
          }}
          onClick={handleRetryFetch}
        >
          Reintentar
        </Button>
      }
    >
      <p className="font-medium">Esto está tardando más de lo previsto.</p>
      <p>Verifica tu conexión o vuelve a intentarlo en unos segundos.</p>
    </SlowStateBanner>
  ) : null;

  const headerSkeleton = (
    <div className="space-y-4">
      <SkeletonHeading className="h-9 w-60" />
    </div>
  );

  const actionsSkeleton = (
    <div className="space-y-4">
      <div className="flex items-center">
        <SkeletonBlock className="h-10 w-full rounded-lg lg:w-80" />
      </div>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between lg:flex-row">
        <SkeletonInput className="lg:w-[30%]" />
        <SkeletonBlock className="h-10 w-full rounded-md lg:w-[20%]" />
      </div>
    </div>
  );

  const contentSkeleton = (
    <div className="space-y-4">
      <TableSkeleton columns={5} rows={6} />
    </div>
  );

  const data = useMemo(() => landlordsData || [], [landlordsData]);

  const filteredData = useMemo(() => {
    if (!data?.length) {
      return [];
    }

    let filtered: Landlord[] = [];
    if (legalType === "pf") {
      filtered = data.filter((landlord) => landlord.legal_type == 1);
    } else if (legalType === "pm") {
      filtered = data.filter((landlord) => landlord.legal_type == 2);
    }

    if (!searchValue) {
      return filtered;
    }

    const lowercasedValue = searchValue.toLowerCase();
    return filtered.filter((landlord) => {
      const {
        name,
        first_surname,
        legal_entity_name,
        email,
        phone_number,
        second_surname,
      } = landlord;
      return (
        name?.toLowerCase().includes(lowercasedValue) ||
        first_surname?.toLowerCase().includes(lowercasedValue) ||
        second_surname?.toLowerCase().includes(lowercasedValue) ||
        legal_entity_name?.toLowerCase().includes(lowercasedValue) ||
        email?.toLowerCase().includes(lowercasedValue) ||
        phone_number?.toString().includes(lowercasedValue)
      );
    });
  }, [data, legalType, searchValue]);

  useEffect(() => {
    const currentTotal = filteredData.length;
    if (legalType === "pf") {
      setPaginationPF((prev) => {
        if (prev.total === currentTotal) return prev;
        return {
          ...prev,
          total: currentTotal,
        };
      });
    } else {
      setPaginationPM((prev) => {
        if (prev.total === currentTotal) return prev;
        return {
          ...prev,
          total: currentTotal,
        };
      });
    }
  }, [filteredData, legalType]);

  const closeModal = () => {
    setIsLandlordModalOpen(false);
    setSelectedLandlord({});
  };

  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  /**
   * Referencia estable. El cuerpo ya era idempotente (`setSearchValue("")` con
   * `searchValue` en "" hace bail-out en React), asi que hoy no rompe; se
   * memoiza para que quien le agregue una escritura NO idempotente manana no
   * reviva el bucle de "Maximum update depth exceeded".
   */
  const handleClearSearch = useCallback(() => {
    setSearchValue("");
  }, []);

  const normalizeString = (value: string): string => {
    if (value === "") {
      return value;
    }
    return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };

  const handleSaveOrUpdateLandlord = async (landlordData: Landlord) => {
    try {
      if (
        Object.keys(selectedLandlord).length > 0 &&
        "id" in selectedLandlord
      ) {
        const mergedLandlord: Landlord = {
          ...(selectedLandlord as Landlord),
          ...landlordData,
        };
        await updateMutation.mutateAsync({
          landlord: mergedLandlord,
          landlordId: selectedLandlord.id!,
        });
        toast.success("Propietario actualizado correctamente.");
      } else {
        await createMutation.mutateAsync(landlordData);
        toast.success("Propietario creado correctamente.");
      }
      closeModal();
    } catch (error) {
      if (Object.keys(selectedLandlord).length > 0) {
        console.error("Error updating landlord:", error);
        toast.error("Error al actualizar el propietario.");
      } else {
        console.error("Error creating landlord:", error);
        toast.error("Error al crear el propietario.");
      }
      throw error;
    }
  };

  return (
    <>
      <PageLoadingContainer
        showSkeleton={showSkeleton}
        slowState={slowStateBanner}
        headerSkeleton={headerSkeleton}
        actionsSkeleton={actionsSkeleton}
        contentSkeleton={contentSkeleton}
        className="mb-6"
      >
        <div>
          <Row
            gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
            justify="start"
            align="middle"
          >
            <Col xs={24} sm={24} md={12} lg={6}>
              <Title level={1} style={{ marginBottom: 5 }}>
                Propietarios
              </Title>
            </Col>
          </Row>
          <Row
            gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
            align="middle"
            style={{ marginBottom: 15, marginTop: 10 }}
          >
            <Col
              xs={24}
              sm={24}
              md={24}
              lg={24}
              style={{
                display: "flex",
                flexDirection: !screen.lg ? "column" : "row",
                justifyContent: "space-between",
                gap: !screen.lg ? 20 : 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Segmented
                  block
                  options={[
                    {
                      label: (
                        <div>
                          <span>Persona Física</span>
                        </div>
                      ),
                      value: "pf",
                    },
                    {
                      label: (
                        <div
                          style={{
                            marginBottom: !screen.lg ? 0 : 8,
                          }}
                        >
                          <span>Persona Moral</span>
                        </div>
                      ),
                      value: "pm",
                    },
                  ]}
                  onChange={(value) => {
                    if (value === "pf") {
                      setPaginationPF({
                        ...paginationPF,
                        current: 1,
                      });
                      setLegalType("pf");
                    } else {
                      setPaginationPM({
                        ...paginationPM,
                        current: 1,
                      });
                      setLegalType("pm");
                    }
                  }}
                  style={{
                    backgroundColor: "#e1e1e1",
                    height: "80%",
                    width: !screen.lg ? "100%" : undefined,
                  }}
                />
              </div>
            </Col>
          </Row>
          <Row
            gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
            align="middle"
            style={{ marginBottom: 15 }}
          >
            <Col
              xs={24}
              sm={24}
              md={24}
              lg={24}
              style={{
                display: "flex",
                flexDirection: !screen.lg ? "column-reverse" : "row",
                justifyContent: "space-between",
                gap: !screen.lg ? 15 : 0,
              }}
            >
              <TableSearch
                reapplyOn={data}
                onClear={handleClearSearch}
                onSearchSubmit={handleSearch}
                label="propietario(s)"
                style={{ width: !screen.lg ? "100%" : "25%" }}
              />
              <Button
                type="primary"
                block
                onClick={() => {
                  setIsLandlordModalOpen(true);
                  setSelectedLandlord({});
                }}
                style={{ width: !screen.lg ? "100%" : "20%" }}
              >
                Registrar Propietario
              </Button>
            </Col>
          </Row>
          <div>
            <LandlordsTable
              data={data}
              setData={() => {}}
              pagination={pagination}
              setPagination={setPagination}
              loading={loading}
              screen={screen}
              setIsLandlordModalOpen={setIsLandlordModalOpen}
              filteredData={filteredData}
              setFilteredData={() => {}}
              legalType={legalType}
              setSelectedLandlord={setSelectedLandlord}
              messageApi={toast}
            />
          </div>
        </div>
      </PageLoadingContainer>
      <ModernLandlordModal
        isOpen={isLandlordModalOpen}
        onOpenChange={setIsLandlordModalOpen}
        onSave={handleSaveOrUpdateLandlord}
        selectedLandlord={selectedLandlord}
      />
    </>
  );
};

export default LandlordsDashboard;
